import {XIcon} from '@heroicons/react/outline';
import {PencilIcon} from '@heroicons/react/solid';
import {isEmpty} from 'lodash-es';
import {useCallback, useImperativeHandle, useState} from 'react';

import {AfriexAdminPermission} from '@afriex/core/domain/enums';

import {Input, Select} from '@/components';
import {NumberInput} from '@/components/common/Input';
import {PermissionsProvider} from '@/components/common/PermissionsProvider';
import {doNothing} from '@/constants/formatters';
import type {TransactionFee} from '@/types';
import {
  currencyPairOptions,
  FeeTransactionChannels,
  limitTypeOptions,
  paymentMethodOptions,
} from '@/types';

const editableInputClasses = 'w-24 mt-2';
const readOnlyInputClasses = 'w-24 border-0 bg-gray-200';
export type FeeEntryRef = {
  getFee: () => TransactionFee | undefined;
};
export type LimitType = 'above' | 'below' | 'none';

/**
 * FeeEntry is a component that allows the user to edit or create a new fee override.
 * It is a controlled component, meaning that it requires a ref to be passed in order to
 * access the current fee value. The ref is passed in as an innerRef prop.
 */
const FeeEntry = ({
  layout = 'col',
  fee,
  editStyle = 'modal',
  onEdit,
  onDelete,
  type = 'override',
  innerRef,
}: {
  layout?: 'row' | 'col';
  fee: TransactionFee;
  editStyle?: 'modal' | 'inline';
  onEdit?: (fee: TransactionFee) => void;
  onDelete?: (fee: TransactionFee) => void;
  type?: 'override' | 'default';
  innerRef?: React.Ref<FeeEntryRef>;
}) => {
  const [transactionType] = useState(fee?.transactionType);
  const [fixedValue, setFixedValue] = useState(
    fee?.baseValue?.toString() || '',
  );
  const [percentage, setPercentage] = useState(fee?.percentValue?.toString());
  const [maxFee, setMaxFee] = useState(fee?.maxFeeValue?.toString());
  const [currencyPair, setCurrencyPair] = useState<string | undefined>(
    fee?.currencyPair,
  );
  const sourceCurrency = currencyPair?.split(':')[0];
  const destinationCurrency = currencyPair?.split(':')[1];
  // `''` (not just `undefined`) is a real value here: selecting "Any" below
  // stores `''` rather than clearing the field (see the `as string) === ''`
  // check a few lines down, which already anticipated this).
  const [selectedInstrument, setInstrument] = useState<
    FeeTransactionChannels | '' | undefined
  >(fee?.paymentMethod);
  const [limitType, setLimitType] = useState<LimitType>(
    fee?.limitType || 'none',
  );
  const [limit, setLimit] = useState<number | undefined>(fee?.limit);
  const inputClasses =
    editStyle === 'inline' ? editableInputClasses : readOnlyInputClasses;

  const disabled = editStyle === 'modal';

  const feeData = useCallback(
    () =>
      ({
        paymentMethod: selectedInstrument,
        baseValue: !isEmpty(fixedValue) ? Number(fixedValue) : undefined,
        percentValue: !isEmpty(percentage) ? Number(percentage) : undefined,
        maxFeeValue: !isEmpty(maxFee) ? Number(maxFee) : undefined,
        limitType: limitType,
        currencyPair: !isEmpty(currencyPair) ? currencyPair : undefined,
        limit: limit,
        type,
      }) as TransactionFee,
    [
      currencyPair,
      fixedValue,
      limit,
      limitType,
      maxFee,
      percentage,
      selectedInstrument,
      type,
    ],
  );

  useImperativeHandle(innerRef, () => ({getFee: () => feeData()}), [feeData]);

  return (
    <div className="override-entry mb-4 w-full">
      <div className="overflow-x-auto hide-scrollbar">
        <div className={`flex flex-${layout === 'row' ? 'col' : 'row'} gap-3`}>
          {type === 'override' && (
            <div className="min-w-max">
              <Input
                className={`${inputClasses} w-32`}
                disabled={disabled}
                name="transactionType"
                value={transactionType}
                onChange={doNothing}
                label="Transaction Type:"
                placeholder={
                  (transactionType as string) === '' ||
                  (transactionType as string) === undefined
                    ? 'Any'
                    : transactionType
                }
              />
            </div>
          )}

          {type === 'override' && (
            <Select
              name="instrument"
              disabled={disabled}
              className="min-w-max"
              bodyClassName={`${inputClasses}`}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                const selectedMethod: FeeTransactionChannels | '' =
                  e.target.value === 'any'
                    ? ''
                    : (
                        Object.create(FeeTransactionChannels) as Record<
                          string,
                          FeeTransactionChannels
                        >
                      )[e.target.value];
                setInstrument(selectedMethod);
              }}
              value={
                (selectedInstrument as string) === '' ||
                (selectedInstrument as string) === undefined
                  ? 'any'
                  : selectedInstrument
              }
              options={paymentMethodOptions}
              label="Payment Method:"
            />
          )}

          {/* Currency Pair */}
          {type === 'override' && (
            <div className="min-w-max">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Currency Pair:
              </label>

              <div className="flex items-center gap-2">
                <Select
                  name="sourceCurrency"
                  disabled={selectedInstrument !== undefined || disabled}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                    const destination = destinationCurrency;
                    const currencyPair = `${e.target.value}:${isEmpty(destination) ? '*' : destination}`;
                    const pair =
                      currencyPair.replace(/\*/g, '')?.length <= 1
                        ? undefined
                        : currencyPair;
                    setCurrencyPair(pair);
                  }}
                  value={sourceCurrency || '*'}
                  options={currencyPairOptions}
                  bodyClassName={`${inputClasses}`}
                />

                <Select
                  name="destinationCurrency"
                  disabled={selectedInstrument !== undefined || disabled}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                    const source = sourceCurrency;
                    const currencyPair = `${isEmpty(source) ? '*' : source}:${e.target.value}`;
                    const pair =
                      currencyPair.replace(/\*/g, '')?.length <= 1
                        ? undefined
                        : currencyPair;
                    setCurrencyPair(pair);
                  }}
                  value={destinationCurrency || '*'}
                  options={currencyPairOptions}
                  bodyClassName={`${inputClasses}`}
                />
              </div>
            </div>
          )}

          {/* Fixed */}
          <div className={`flex flex-${layout} min-w-max`}>
            <NumberInput
              className={`${inputClasses}`}
              min={0}
              disabled={disabled}
              name="baseValue"
              value={fixedValue}
              label="Fixed ($):"
              onValueChange={(rawValue) => {
                setFixedValue(rawValue);
              }}
            />
          </div>

          {/* Percentage */}
          <div className={`flex flex-${layout} min-w-max`}>
            <NumberInput
              className={`${inputClasses}`}
              value={percentage || ''}
              disabled={disabled}
              min={0}
              name="percentValue"
              onValueChange={(rawValue) => {
                setPercentage(rawValue);
              }}
              label="Percentage (%):"
            />
          </div>

          {/* Max Fee */}
          <div className={`flex flex-${layout} min-w-max`}>
            <NumberInput
              className={`${inputClasses}`}
              name="maxFee"
              value={maxFee || ''}
              disabled={disabled}
              min={0}
              onValueChange={(rawValue) => {
                setMaxFee(rawValue);
              }}
              label="Max Fee ($):"
            />
          </div>

          {type === 'override' && (
            <>
              <Select
                name="limitType"
                value={(limitType as string) === '' ? 'none' : limitType}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                  const selectedValue =
                    e.target.value === 'none' ? '' : e.target.value;
                  setLimitType(selectedValue as LimitType);
                }}
                options={limitTypeOptions}
                label="Limit Type:"
                disabled={disabled}
                bodyClassName={`${inputClasses}`}
                className={`flex flex-${layout} min-w-max`}
              />

              <div className={`flex flex-${layout} min-w-max`}>
                <Input
                  type="number"
                  className={`${inputClasses} w-24`}
                  min={0}
                  name="limit"
                  value={limit}
                  disabled={limitType === undefined || disabled}
                  required={limitType !== undefined}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setLimit(Number(e.target.value));
                  }}
                  label="Limit Value:"
                  placeholder={limit ? limit.toString() : '0'}
                />
              </div>
            </>
          )}

          <PermissionsProvider
            permission={AfriexAdminPermission.TransactionFeeCreate}
          >
            {type === 'override' && (
              <div className="flex flex-row items-end justify-between gap-3 mr-4">
                <div
                  className="cursor-pointer border-2 p-1 border-gray-300 dark:border-gray-500 rounded-md"
                  onClick={() => onEdit?.(fee)}
                >
                  <PencilIcon className="w-5 h-5 text-successful-500" />
                </div>

                <div
                  className="cursor-pointer border-2 p-1 border-gray-300 dark:border-gray-500 rounded-md"
                  onClick={() => fee && onDelete?.(fee)}
                >
                  <XIcon className="w-5 h-5 text-red-500" />
                </div>
              </div>
            )}
          </PermissionsProvider>
        </div>
      </div>
    </div>
  );
};

export default FeeEntry;
